import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { ArrowUpRight, AlertTriangle, DollarSign, Package, Activity } from "lucide-react";

const STATUS_ORDER = ["in_use", "storage", "repair", "retired"];
const STATUS_LABEL = { in_use: "In Use", storage: "Storage", repair: "Repair", retired: "Retired" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        testid="dashboard-header"
        eyebrow="[00] Control Room"
        title="Overview"
        subtitle="Real-time signal across every tracked asset in the organization. All monetary figures are NZD and GST exclusive."
      />

      <div className="p-8 space-y-8">
        {/* KPI Strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E8E3D6] border border-[#E5E0D5]">
          <KpiCell
            label="Total Assets"
            value={loading ? "…" : stats?.total_assets ?? 0}
            icon={Package}
            testid="kpi-total"
          />
          <KpiCell
            label="In Use"
            value={loading ? "…" : stats?.by_status?.in_use ?? 0}
            icon={Activity}
            tint="text-[#2D7A4F]"
            testid="kpi-in-use"
          />
          <KpiCell
            label="In Repair"
            value={loading ? "…" : stats?.by_status?.repair ?? 0}
            icon={AlertTriangle}
            tint="text-[#B45309]"
            testid="kpi-repair"
          />
          <KpiCell
            label="Total Value"
            value={loading ? "…" : `NZ$${Math.round(stats?.total_value || 0).toLocaleString()}`}
            icon={DollarSign}
            testid="kpi-value"
          />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-[#E5E0D5] bg-white p-6" data-testid="status-chart-card">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="uppercase-label">Distribution</div>
                <h3 className="text-lg font-medium tracking-tight mt-1">Assets by Status</h3>
              </div>
              <Link to="/assets" data-testid="link-assets" className="text-xs font-mono uppercase tracking-wider text-[#7A7A7A] hover:text-[#0F0F0F]">
                View all <ArrowUpRight className="inline w-3 h-3" />
              </Link>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={STATUS_ORDER.map((s) => ({ name: STATUS_LABEL[s], value: stats?.by_status?.[s] ?? 0 }))}>
                  <CartesianGrid stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="name" stroke="#71717a" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                  <YAxis stroke="#71717a" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "#0a0a0a",
                      border: "1px solid #222",
                      fontFamily: "IBM Plex Mono",
                      fontSize: 12,
                      color: "#fff",
                    }}
                    cursor={{ fill: "rgba(0,71,255,0.08)" }}
                  />
                  <Bar dataKey="value" fill="#1A1A1A" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-[#E5E0D5] bg-white p-6" data-testid="category-card">
            <div className="uppercase-label">By Category</div>
            <h3 className="text-lg font-medium tracking-tight mt-1 mb-5">Inventory Mix</h3>
            <div className="space-y-3">
              {(stats?.by_category || []).slice(0, 8).map((c) => {
                const total = stats?.total_assets || 1;
                const pct = Math.round((c.count / total) * 100);
                return (
                  <div key={c.category}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="font-mono text-xs text-[#3A3A3A]">{c.category}</span>
                      <span className="font-mono text-xs text-[#7A7A7A]">{c.count} · {pct}%</span>
                    </div>
                    <div className="h-1 bg-[#E8E3D6]">
                      <div className="h-full bg-[#F37B2A]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {!loading && (stats?.by_category || []).length === 0 && (
                <div className="text-xs font-mono text-[#9A9A9A]">No assets yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* Warranty alerts + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-[#E5E0D5] bg-white" data-testid="warranty-card">
            <div className="px-6 py-4 border-b border-[#E5E0D5] flex items-center justify-between">
              <div>
                <div className="uppercase-label">Alerts</div>
                <h3 className="text-base font-medium tracking-tight mt-1">Warranty Expiring (60d)</h3>
              </div>
              <AlertTriangle className="w-4 h-4 text-[#B45309]" strokeWidth={1.5} />
            </div>
            <div className="divide-y divide-[#E5E0D5]">
              {(stats?.warranty_expiring || []).map((a) => (
                <Link
                  to={`/assets/${a.id}`}
                  key={a.id}
                  data-testid={`warranty-row-${a.id}`}
                  className="flex items-center justify-between px-6 py-3 hover:bg-[#F0EDE3] transition-colors"
                >
                  <div>
                    <div className="font-mono text-xs text-[#7A7A7A]">{a.asset_tag}</div>
                    <div className="text-sm">{a.name}</div>
                  </div>
                  <div className="font-mono text-xs text-[#B45309]">{a.warranty_expiry}</div>
                </Link>
              ))}
              {!loading && (stats?.warranty_expiring || []).length === 0 && (
                <div className="px-6 py-6 text-xs font-mono text-[#9A9A9A]">No expiring warranties.</div>
              )}
            </div>
          </div>

          <div className="border border-[#E5E0D5] bg-white" data-testid="recent-card">
            <div className="px-6 py-4 border-b border-[#E5E0D5]">
              <div className="uppercase-label">Activity</div>
              <h3 className="text-base font-medium tracking-tight mt-1">Recent Assets</h3>
            </div>
            <div className="divide-y divide-[#E5E0D5]">
              {(stats?.recent_assets || []).map((a) => (
                <Link
                  to={`/assets/${a.id}`}
                  key={a.id}
                  data-testid={`recent-row-${a.id}`}
                  className="flex items-center justify-between px-6 py-3 hover:bg-[#F0EDE3] transition-colors"
                >
                  <div>
                    <div className="font-mono text-xs text-[#7A7A7A]">{a.asset_tag}</div>
                    <div className="text-sm">{a.name}</div>
                  </div>
                  <StatusPill status={a.status} />
                </Link>
              ))}
              {!loading && (stats?.recent_assets || []).length === 0 && (
                <div className="px-6 py-6 text-xs font-mono text-[#9A9A9A]">
                  No assets registered yet. <Link to="/assets/new" className="text-[#1A1A1A]">Create one →</Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading && <Skeleton className="h-4 w-32 bg-[#E8E3D6]" />}
      </div>
    </div>
  );
}

function KpiCell({ label, value, icon: Icon, tint = "text-[#0F0F0F]", testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="uppercase-label">{label}</div>
        <Icon className="w-4 h-4 text-[#BABABA]" strokeWidth={1.5} />
      </div>
      <div className={`font-mono text-3xl mt-4 tracking-tight ${tint}`}>{value}</div>
    </div>
  );
}
